import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  Tabs,
  Tab,
  IconButton,
  Alert,
  Snackbar,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack,
  Add,
  MoreVert,
  Edit,
  Delete,
  Reply,
  LocalShipping,
} from '@mui/icons-material';
import correspondenceService from '../services/correspondenceService';
import { usePermissions } from '../../../hooks/usePermissions';
import ThreadForm from '../components/ThreadForm';
import ThreadTable from '../components/ThreadTable';
import DocumentSection from '../components/DocumentSection';
import CorrespondenceMetrics from '../components/CorrespondenceMetrics';
import CorrespondenceTraceability from '../components/CorrespondenceTraceability';
import LoadingSpinner from '../../../components/LoadingSpinner';
import PageMasthead from '../../../components/PageMasthead';

const CorrespondenceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [correspondence, setCorrespondence] = useState(null);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openThreadModal, setOpenThreadModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openReplyModal, setOpenReplyModal] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [activeTab, setActiveTab] = useState(0);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadCorrespondence();
  }, [id]);

  const loadCorrespondence = async () => {
    try {
      setLoading(true);
      const data = await correspondenceService.getById(id);
      setCorrespondence(data);
      setThreads(data.threads || []);
    } catch (error) {
      console.error('Error loading correspondence:', error);
      showSnackbar('Error al cargar la correspondencia', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveThread = async (data) => {
    try {
      await correspondenceService.createThread(id, data);
      showSnackbar('Hilo creado exitosamente');
      setOpenThreadModal(false);
      loadCorrespondence();
    } catch (error) {
      console.error('Error saving thread:', error);
      showSnackbar(error.response?.data?.error || 'Error al guardar el hilo', 'error');
      throw error;
    }
  };

  const handleEdit = async (data) => {
    try {
      await correspondenceService.update(id, data);
      showSnackbar('Correspondencia actualizada');
      setOpenEditModal(false);
      loadCorrespondence();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error al actualizar', 'error');
      throw error;
    }
  };

  const handleDelete = async () => {
    try {
      setSubmitting(true);
      await correspondenceService.delete(id);
      showSnackbar('Correspondencia eliminada');
      setOpenDeleteDialog(false);
      navigate('/correspondences');
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error al eliminar', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async (data) => {
    try {
      const result = await correspondenceService.respond(id, {
        response: data.message,
        cc: data.cc,
        templateId: data.templateId,
        documentKey: data.documentKey,
        documentName: data.documentName,
        documentKeys: data.documentKeys,
        sign: data.sign,
      });
      const signatureToken = result?.signatureToken;
      showSnackbar(
        signatureToken
          ? `Respuesta enviada y firmada electronicamente (Token: ${signatureToken.slice(0, 8)}...)`
          : 'Respuesta enviada exitosamente'
      );
      setOpenReplyModal(false);
      loadCorrespondence();
    } catch (error) {
      showSnackbar(error.response?.data?.error || 'Error al responder', 'error');
      throw error;
    }
  };

  const handleMarkDelivered = async () => {
    try {
      await correspondenceService.markAsDelivered(id);
      showSnackbar('Marcada como entregada en fisico');
      setMenuAnchor(null);
      loadCorrespondence();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error al actualizar', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const getStatusLabel = (status) => {
    const labels = {
      registered: 'Registrada',
      assigned: 'Asignada',
      in_progress: 'En Progreso',
      responded: 'Respondida',
      closed: 'Cerrada',
    };
    return labels[status] || status;
  };

  const InfoField = ({ label, children }) => (
    <Box>
      <Typography
        sx={{
          fontSize: '0.6875rem',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: 'rgba(29,31,32,0.55)',
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Box>{children}</Box>
    </Box>
  );

  if (loading) {
    return <LoadingSpinner message="Cargando correspondencia..." />;
  }

  if (!correspondence) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Correspondencia no encontrada</Alert>
      </Box>
    );
  }

  const sender = correspondence.users_correspondences_sender_idTousers;
  const recipient = correspondence.users_correspondences_recipient_idTousers;
  const corrUser = correspondence.correspondenceUser;
  const corrType = correspondence.correspondenceType;
  const isExternal = correspondence.user_type === 'external';
  const isClosed = correspondence.status === 'closed';

  let parsedComments = null;
  if (isExternal && correspondence.comments) {
    try {
      parsedComments = typeof correspondence.comments === 'string'
        ? JSON.parse(correspondence.comments)
        : correspondence.comments;
    } catch {
      parsedComments = null;
    }
  }

  return (
    <Box>
      {/* Masthead */}
      <PageMasthead
        title={correspondence.in_settled || 'Correspondencia'}
        subtitle={`Correspondencia · ${correspondence.title}`}
        stat={getStatusLabel(correspondence.status)}
        statLabel="Estado"
      />

      {/* Back + Actions row */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, mb: 3, gap: 1, flexWrap: 'wrap' }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/correspondences')}
          sx={{ color: '#5980a6', fontWeight: 700, fontSize: '0.9375rem' }}
        >
          Correspondencia
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Reply />}
            size="small"
            onClick={() => setOpenReplyModal(true)}
            disabled={isClosed}
          >
            Responder
          </Button>

          {correspondence.physical_delivered && (
            <Chip label="Entregado en fisico" size="small" variant="outlined" sx={{ borderColor: 'rgba(29,31,32,0.16)' }} />
          )}

          <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{ width: 36, height: 36 }}>
            <MoreVert sx={{ fontSize: 20 }} />
          </IconButton>
          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
            {hasPermission('correspondence.update') && !isClosed && (
              <MenuItem onClick={() => { setMenuAnchor(null); setOpenEditModal(true); }}>
                <Edit fontSize="small" sx={{ mr: 1 }} /> Editar
              </MenuItem>
            )}
            {!correspondence.physical_delivered && !isClosed && (
              <MenuItem onClick={handleMarkDelivered}>
                <LocalShipping fontSize="small" sx={{ mr: 1 }} /> Marcar como entregado en fisico
              </MenuItem>
            )}
            {correspondence.physical_delivered && (
              <MenuItem onClick={() => { setMenuAnchor(null); }}>
                <LocalShipping fontSize="small" sx={{ mr: 1 }} /> Quitar marca de entregado en fisico
              </MenuItem>
            )}
            {hasPermission('correspondence.delete') && !isClosed && (
              <MenuItem onClick={() => { setMenuAnchor(null); setOpenDeleteDialog(true); }} sx={{ color: 'error.main' }}>
                <Delete fontSize="small" sx={{ mr: 1 }} /> Eliminar
              </MenuItem>
            )}
          </Menu>
        </Box>
      </Box>

      {/* Info card */}
      <Paper sx={{ p: { xs: 2.5, sm: 3.5 }, mb: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            {correspondence.title}
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: '20px', mb: 3 }}>
          <InfoField label="Fecha de Registro">
            <Typography variant="body1">
              {correspondence.createdAt
                ? new Date(correspondence.createdAt).toLocaleString('es-ES')
                : '\u2014'}
            </Typography>
          </InfoField>

          <InfoField label="Empresa">
            <Typography variant="body1">
              {correspondence.company?.name}
            </Typography>
            {correspondence.company?.short && (
              <Typography variant="body2" color="text.secondary">
                ({correspondence.company.short})
              </Typography>
            )}
          </InfoField>

          <InfoField label="Tipo de Correspondencia">
            <Typography variant="body1">
              {corrType?.name || '\u2014'}
            </Typography>
            {corrType?.area && (
              <Typography variant="body2" color="text.secondary">
                Area: {corrType.area.name}
              </Typography>
            )}
          </InfoField>

          <InfoField label="Remitente / Tipo">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1">
                {isExternal && parsedComments
                  ? parsedComments.senderName
                  : corrUser?.name || `ID: ${correspondence.user_id}`}
              </Typography>
              <Chip
                label={isExternal ? 'Externo' : 'Interno'}
                size="small"
                variant="outlined"
                sx={{ borderColor: 'rgba(29,31,32,0.16)', fontSize: '0.6875rem' }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary">
              {isExternal && parsedComments
                ? parsedComments.senderEmail
                : corrUser?.email}
              {isExternal && parsedComments?.senderPhone && ` · ${parsedComments.senderPhone}`}
            </Typography>
          </InfoField>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: '20px', mb: 3 }}>
          {correspondence.sender_identification && (
            <InfoField label="Identificacion">
              <Typography variant="body1" fontWeight={500}>
                {correspondence.sender_identification.documentType} {correspondence.sender_identification.documentNumber}
              </Typography>
            </InfoField>
          )}

          {correspondence.out_settled && (
            <InfoField label="Radicado de Salida">
              <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                {correspondence.out_settled}
              </Typography>
            </InfoField>
          )}

          {sender && (
            <InfoField label="Creado por">
              <Typography variant="body1">{sender.name}</Typography>
              <Typography variant="body2" color="text.secondary">{sender.email}</Typography>
            </InfoField>
          )}

          {recipient && (
            <InfoField label="Asignado a">
              <Typography variant="body1">{recipient.name}</Typography>
              <Typography variant="body2" color="text.secondary">{recipient.email}</Typography>
            </InfoField>
          )}
        </Box>

        {(parsedComments?.message || (!isExternal && correspondence.comments)) && (
          <Box sx={{ borderTop: '1px solid rgba(29,31,32,0.16)', pt: 2.5, mb: 2 }}>
            <InfoField label="Mensaje">
              <Typography
                variant="body1"
                component="div"
                dangerouslySetInnerHTML={{ __html: isExternal ? parsedComments.message : correspondence.comments }}
              />
            </InfoField>
          </Box>
        )}

        {Array.isArray(correspondence.attachments) && correspondence.attachments.length > 0 && (
          <InfoField label="Documentos Adjuntos">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
              {correspondence.attachments.map((att, i) => (
                <Chip
                  key={i}
                  label={att.name || att.key}
                  size="small"
                  variant="outlined"
                  sx={{ borderColor: 'rgba(29,31,32,0.16)' }}
                />
              ))}
            </Box>
          </InfoField>
        )}
      </Paper>

      {/* Tabs */}
      <Box sx={{ mb: 3, display: 'inline-flex' }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Hilos de Conversacion" />
          <Tab label="Documentos" />
          <Tab label="Metricas" />
          <Tab label="Trazabilidad" />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" component="h2">
              Hilos de Conversacion
            </Typography>
            {hasPermission('correspondence.thread') && !isClosed && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setOpenThreadModal(true)}
              >
                Crear Hilo
              </Button>
            )}
          </Box>

          <ThreadTable threads={threads} readOnly={isClosed} />
        </Box>
      )}

      {activeTab === 1 && (
        <DocumentSection correspondenceId={id} readOnly={isClosed} />
      )}

      {activeTab === 2 && (
        <CorrespondenceMetrics correspondenceId={id} />
      )}

      {activeTab === 3 && (
        <CorrespondenceTraceability correspondenceId={id} />
      )}

      <ThreadForm
        open={openThreadModal}
        onClose={() => setOpenThreadModal(false)}
        onSave={handleSaveThread}
        correspondenceId={id}
      />

      {/* Reply Modal */}
      <ThreadForm
        open={openReplyModal}
        onClose={() => setOpenReplyModal(false)}
        onSave={handleReply}
        correspondenceId={id}
        isReply
        companyId={correspondence?.companyId}
      />

      {/* Edit Dialog */}
      <Dialog open={openEditModal} onClose={() => setOpenEditModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Editar Correspondencia</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Para editar todos los campos, use la lista de correspondencias.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditModal(false)}>Cerrar</Button>
          <Button
            variant="contained"
            onClick={() => {
              setOpenEditModal(false);
              navigate('/correspondences');
            }}
          >
            Ir a la lista
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirmar eliminacion</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Esta seguro de eliminar la correspondencia "{correspondence.title}"? Esta accion no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} disabled={submitting}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={submitting}>
            {submitting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CorrespondenceDetail;
